import { Tooltip, tokens } from "@blurb/codex-react";
import { createStyles, Divider, Stack, Text } from "@mantine/core";
import { isGraphQLError } from "@rpi/openapi-api";
import { IconV3 } from "@rpi/openapi-core";
import { RpaTextInput } from "../../components/inputFields/RpaTextInput";
import { RpiTextField } from "../../components/RpiTextField";
import { filterByMatchingAttributes } from "../../hooks/useStandardProducts";
import { formatCurrency } from "../../utils/misc.util";
import { getBookPriceInfoUSD, PriceInfo } from "../../utils/pricing.util";
import { useProductCreateContext } from "./CustomerProductCreateView";
import { StepProps, ProductCreateNavButtons, attributesForFiltering } from "./ProductCreateNavigator";

const useNameAndSkuStyles = createStyles({
  fieldContainer: {
    padding: "var(--codex-spacing-2) 0 0 0",
    gap: "var(--codex-spacing-6)",
  },
});

export function NameAndSkuStep({ state, setState, standardProducts }: StepProps) {
  const { classes } = useNameAndSkuStyles();
  const { onSave, loading } = useProductCreateContext();

  const filteredProducts = filterByMatchingAttributes(standardProducts, attributesForFiltering(state));
  if (filteredProducts.length !== 1) {
    throw new Error(`Product create filter didn't resolve to a single openapi SKU -  ${filteredProducts}`);
  }
  const openapiProduct = filteredProducts[0];
  const priceInfo = getBookPriceInfoUSD(openapiProduct);

  const onDone = () => {
    setState((draft) => {
      const nameField = draft.customerProductName;
      const skuField = getSkuValue(openapiProduct.openapiSku, draft.customerProductSku);
      if (!nameField || !skuField) {
        draft.nextFailed = true;
      } else {
        draft.nextFailed = false;

        onSave(nameField, skuField, openapiProduct.id, (error) => {
          if (isGraphQLError(error)) {
            setState((draft) => {
              let message = error.message;
              if (error.message.includes("sku already exists")) {
                message = "A product with that SKU already exists";
              }
              draft.saveError = message;
              draft.nextFailed = true;
            });
          }
        });
      }
    });
  };

  const nameError = state.nextFailed && !state.customerProductName ? "Please enter a product name" : undefined;
  const skuValue = getSkuValue(openapiProduct.openapiSku, state.customerProductSku);
  const skuEmptyError = state.nextFailed && !skuValue ? "Please enter a Product SKU" : undefined;

  return (
    <Stack>
      <Stack className={classes.fieldContainer}>
        <RpaTextInput
          label="Product Name"
          value={state.customerProductName}
          error={nameError}
          onChange={(event) =>
            setState((draft) => {
              draft.customerProductName = event.currentTarget.value;
              draft.nextFailed = false;
            })
          }
          disabled={loading}
        />
        <RpaTextInput
          label="Product SKU"
          value={skuValue}
          error={state.saveError || skuEmptyError}
          onChange={(event) =>
            setState((draft) => {
              draft.saveError = "";
              draft.customerProductSku = event.currentTarget.value;
              draft.nextFailed = false;
            })
          }
          disabled={loading}
        />
      </Stack>
      <ProductCreateNavButtons state={state} setState={setState} onDone={onDone} loading={loading} />
      <AdditionalInfo openapiSku={openapiProduct.openapiSku} customerSku={skuValue} priceInfo={priceInfo} />
    </Stack>
  );
}

// this defaults the openapiSku when the customer sku is undefined,
// but still returns empty string if customer sku has been set to that by the user
function getSkuValue(openapiSku: string, customerSku?: string) {
  return customerSku ?? openapiSku;
}

const useAdditionalInfoStyles = createStyles({
  container: {
    display: "flex",
    flexDirection: "column",
    gap: "var(--codex-spacing-4)",
    padding: "var(--codex-spacing-4)",
    borderRadius: "var(--codex-radius-lg)",
    backgroundColor: tokens.color.semantic.bg.infoSubtlest,
  },
  header: {
    "font-weight": "var(--codex-font-weight-semibold)",
    fontSize: "var(--codex-font-size-lg)",
    lineHeight: "var(--codex-font-line-height-normal)",
  },
  labelWithIcon: {
    display: "inline-flex",
    alignItems: "center",
    gap: "var(--codex-spacing-1)",
  },
  infotip: {
    display: "inline-flex",
  },
});

type AdditionalInfoProps = {
  openapiSku: string;
  customerSku: string;
  priceInfo: PriceInfo;
};
function AdditionalInfo({ priceInfo, openapiSku, customerSku }: AdditionalInfoProps) {
  const { classes } = useAdditionalInfoStyles();

  if (!priceInfo.usd) {
    return null;
  }

  return (
    <div className={classes.container}>
      <Text className={classes.header}>Additional Information</Text>
      <Divider />
      <RpiTextField label={`Base price (First ${priceInfo.minPageCount} pages)`}>
        {formatCurrency(priceInfo.usd.currency, priceInfo.usd.basePrice, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}
      </RpiTextField>
      <RpiTextField label="Additional Price Per Page">
        {formatCurrency(priceInfo.usd.currency, priceInfo.usd.perPageCost, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}
      </RpiTextField>
      <RpiTextField copyable label="Product SKU">
        {customerSku}
      </RpiTextField>
      <RpiTextField
        copyable
        label={
          <div className={classes.labelWithIcon}>
            Manufacturing SKU
            <Tooltip content="This is the SKU used by the printing manufacturer">
              <div className={classes.infotip}>
                <IconV3 icon="info" size={16} color={tokens.color.semantic.icon.subtle} />
              </div>
            </Tooltip>
          </div>
        }
      >
        {openapiSku}
      </RpiTextField>
    </div>
  );
}
